import { z } from 'zod';
import { IsoDateTimeSchema } from './common.js';
export const PlaybackEventTypeSchema = z.enum([
    'play_start',
    'play_end',
    'play_interrupted',
    'play_failed',
]);
export const DevicePlaybackEventSchema = z.object({
    /** Missing on older players: the server stores it as play_start. */
    eventType: PlaybackEventTypeSchema.optional(),
    /** Set by the player so a resent batch is stored only once. */
    clientEventId: z.string().uuid().optional(),
    mediaAssetId: z.string().uuid().optional(),
    playlistId: z.string().uuid().optional(),
    playlistVersion: z.number().int().nonnegative().optional(),
    itemPosition: z.number().int().nonnegative().optional(),
    playedAt: IsoDateTimeSchema,
    durationMs: z.number().int().nonnegative().optional(),
    errorCode: z.string().min(1).max(64).optional(),
    errorMessage: z.string().max(500).optional(),
});
export const PlaybackQueueLossSchema = z.object({
    queueId: z.string().uuid(),
    droppedEvents: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
});
export const IngestPlaybackEventsRequestSchema = z
    .object({
    events: z.array(DevicePlaybackEventSchema).max(100),
    queueLoss: PlaybackQueueLossSchema.optional(),
})
    .refine((body) => body.events.length > 0 || body.queueLoss !== undefined, {
    message: 'Provide playback events or a queue-loss report',
});
/**
 * What the server accepts at the envelope level: each event is then validated on its own,
 * so one bad event is rejected by id instead of failing the whole batch.
 */
export const IngestPlaybackEventsEnvelopeSchema = z
    .object({
    events: z.array(z.unknown()).max(100),
    queueLoss: PlaybackQueueLossSchema.optional(),
})
    .refine((body) => body.events.length > 0 || body.queueLoss !== undefined, {
    message: 'Provide playback events or a queue-loss report',
});
export const IngestPlaybackEventsResponseSchema = z.object({
    /** Every event in the batch, duplicates included: the player can clear its queue on 2xx. */
    accepted: z.number().int().nonnegative(),
    /** Events skipped because this device already sent the same clientEventId. */
    duplicates: z.number().int().nonnegative().optional(),
    /** clientEventIds of events that failed validation and were not stored. */
    rejectedClientEventIds: z.array(z.string()).optional(),
    /** Confirms the persisted cumulative loss count for this queue. */
    queueLossAck: PlaybackQueueLossSchema.optional(),
});
export const PlaybackEventSchema = z.object({
    id: z.string().min(1),
    deviceId: z.string().min(1),
    organisationId: z.string().nullable(),
    screenId: z.string().nullable(),
    playlistId: z.string().nullable(),
    playlistVersion: z.number().int().nullable(),
    mediaAssetId: z.string().nullable(),
    eventType: PlaybackEventTypeSchema,
    clientEventId: z.string().nullable(),
    itemPosition: z.number().int().nullable(),
    playedAt: IsoDateTimeSchema,
    durationMs: z.number().int().nullable(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
});
export const PlaybackSummaryBucketSchema = z.object({
    key: z.string(),
    playCount: z.number().int().nonnegative(),
});
export const PlaybackSummarySchema = z.object({
    totalPlays: z.number().int().nonnegative(),
    byDevice: z.array(PlaybackSummaryBucketSchema),
    byMedia: z.array(PlaybackSummaryBucketSchema),
    byDay: z.array(PlaybackSummaryBucketSchema),
});
export const MembershipAlertSettingsSchema = z.object({
    emailAlertsEnabled: z.boolean(),
});
export const UpdateMembershipAlertSettingsSchema = z.object({
    emailAlertsEnabled: z.boolean(),
});
//# sourceMappingURL=reports.js.map